import {
  Drawer, Stack, Text, Badge, Group, Divider, Box, Skeleton, Avatar, ScrollArea,
} from '@mantine/core'
import { IconPaperclip } from '@tabler/icons-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useJiraIssueDetail, useJiraIssueComments } from '@/hooks/useJiraData'
import { formatTimeAgo } from '@/utils/formatTimeAgo'
import type { JiraAttachmentMeta } from '@/api/jira'

interface JiraIssueDetailDrawerProps {
  jiraKey: string | null
  opened: boolean
  onClose: () => void
}

const STATUS_CATEGORY_COLORS: Record<string, string> = {
  todo: 'blue',
  in_progress: 'yellow',
  done: 'green',
}

const PRIORITY_COLORS: Record<string, string> = {
  Highest: 'red',
  High: 'orange',
  Medium: 'yellow',
  Low: 'teal',
  Lowest: 'gray',
}

export function JiraIssueDetailDrawer({ jiraKey, opened, onClose }: JiraIssueDetailDrawerProps) {
  const { data: detail, isLoading: detailLoading } = useJiraIssueDetail(opened ? jiraKey : null)
  const { data: comments, isLoading: commentsLoading } = useJiraIssueComments(opened ? jiraKey : null)

  const attachments = (detail?.raw_fields?._attachments as JiraAttachmentMeta[] | undefined) ?? []

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text fw={700} size="sm">{jiraKey}</Text>
          {detail && (
            <Badge size="xs" color={STATUS_CATEGORY_COLORS[detail.status_category] ?? 'gray'} variant="light">
              {detail.status_name}
            </Badge>
          )}
        </Group>
      }
      position="right"
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {detailLoading ? (
        <Stack gap="sm">
          <Skeleton height={24} />
          <Skeleton height={16} w="60%" />
          <Skeleton height={80} />
        </Stack>
      ) : detail ? (
        <Stack gap="md">
          {/* Summary */}
          <Text fw={600} size="md">{detail.summary}</Text>

          {/* Meta info */}
          <Group gap="xl" wrap="wrap">
            <Box>
              <Text size="xs" c="dimmed" mb={2}>Tipo</Text>
              <Badge size="xs" variant="outline">{detail.issue_type || '—'}</Badge>
            </Box>
            <Box>
              <Text size="xs" c="dimmed" mb={2}>Prioridade</Text>
              <Badge
                size="xs"
                color={PRIORITY_COLORS[detail.priority_name] ?? 'gray'}
                variant="light"
              >
                {detail.priority_name || '—'}
              </Badge>
            </Box>
            <Box>
              <Text size="xs" c="dimmed" mb={2}>Responsável</Text>
              <Text size="xs">{detail.assignee_jira_account_id || '—'}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed" mb={2}>Atualizado</Text>
              <Text size="xs">{formatTimeAgo(detail.jira_updated_at)}</Text>
            </Box>
          </Group>

          {/* Description from raw_fields */}
          {typeof detail.raw_fields?.description_text === 'string' && detail.raw_fields.description_text && (
            <>
              <Divider my="xs" />
              <Text size="sm" fw={500}>Descrição</Text>
              <Box
                p="sm"
                style={{
                  background: 'var(--mantine-color-default)',
                  borderRadius: 'var(--mantine-radius-sm)',
                  border: '1px solid var(--mantine-color-default-border)',
                }}
              >
                <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                  {detail.raw_fields.description_text}
                </Text>
              </Box>
            </>
          )}

          {/* Comments Section */}
          <Divider my="xs" />
          <Text fw={600} size="sm">
            Comentários {commentsLoading ? '' : `(${(comments ?? []).length})`}
          </Text>

          {commentsLoading ? (
            <Stack gap="xs">
              <Skeleton height={60} />
              <Skeleton height={60} />
            </Stack>
          ) : (comments ?? []).length === 0 ? (
            <Text size="xs" c="dimmed">Nenhum comentário.</Text>
          ) : (
            <Stack gap="sm">
              {(comments ?? []).map((comment) => (
                <Box
                  key={comment.id}
                  p="sm"
                  style={{
                    borderRadius: 'var(--mantine-radius-sm)',
                    border: '1px solid var(--mantine-color-default-border)',
                    background: comment.is_from_consoleai
                      ? 'var(--mantine-color-blue-light)'
                      : 'var(--mantine-color-default)',
                  }}
                >
                  <Group gap="xs" mb={4}>
                    {comment.author_avatar_url ? (
                      <Avatar src={comment.author_avatar_url} size={20} radius="xl" />
                    ) : (
                      <Avatar size={20} radius="xl" color="gray">
                        {comment.author_display_name?.charAt(0) ?? '?'}
                      </Avatar>
                    )}
                    <Text size="xs" fw={500}>{comment.author_display_name}</Text>
                    <Text size="xs" c="dimmed">{formatTimeAgo(comment.created)}</Text>
                    {comment.is_from_consoleai && (
                      <Badge size="xs" color="blue" variant="dot">ConsoleAI</Badge>
                    )}
                  </Group>

                  {/* Rich text: body_markdown with fallback to body_text */}
                  {(comment as { body_markdown?: string }).body_markdown ? (
                    <Box style={{ fontSize: 'var(--mantine-font-size-xs)' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {(comment as { body_markdown?: string }).body_markdown!}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{comment.body_text}</Text>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {/* Attachments Section */}
          {attachments.length > 0 && (
            <>
              <Divider my="sm" />
              <Text fw={600} size="sm" mb="xs">
                Anexos ({attachments.length})
              </Text>
              <Stack gap="xs">
                {attachments.map((att) => (
                  <Group
                    key={att.id}
                    justify="space-between"
                    p="xs"
                    style={{
                      borderRadius: 'var(--mantine-radius-sm)',
                      background: 'var(--mantine-color-body)',
                      border: '1px solid var(--mantine-color-default-border)',
                    }}
                  >
                    <Group gap="xs">
                      <IconPaperclip size={14} />
                      <Text size="xs">{att.filename}</Text>
                      {att.size > 0 && (
                        <Text size="xs" c="dimmed">
                          {Math.round(att.size / 1024)}KB
                        </Text>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">{att.author}</Text>
                  </Group>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      ) : (
        <Text c="dimmed" size="sm">Issue não encontrada.</Text>
      )}
    </Drawer>
  )
}
